import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import {
  signInWithEmail,
  signUpWithEmail,
  signInWithGoogle,
  signInWithMicrosoft,
  signInWithApple,
  getSignInMethodsForEmail,
} from '../lib/firebase';
import { sendPasswordReset } from '../lib/passwordReset';
import { useI18n } from '../i18n/i18n';
import { useAuth } from './AuthProvider';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'login' | 'signup';
  onModeChange?: (mode: 'login' | 'signup') => void;
}

export const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, mode, onModeChange }) => {
  const [currentMode, setCurrentMode] = useState<'login' | 'signup'>(mode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const [error, setError] = useState<string | null>(null);
  const { t } = useI18n();
  const { refreshUser, isEmailVerified, setVerificationModalOpen } = useAuth();
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [registeredPassword, setRegisteredPassword] = useState('');
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  // Update internal mode when prop changes
  useEffect(() => {
    setCurrentMode(mode);
  }, [mode]);

  // Notify AuthProvider when verification modal state changes
  useEffect(() => {
    setVerificationModalOpen(showVerificationMessage && isOpen);
    
    // Cleanup when component unmounts
    return () => {
      setVerificationModalOpen(false);
    };
  }, [showVerificationMessage, isOpen, setVerificationModalOpen]);

  // Auto-detect when email becomes verified while modal is open
  useEffect(() => {
    if (showVerificationMessage && isEmailVerified) {
      
      // Show success message
      toast({
        title: t('auth.verification.emailVerified'),
        description: t('auth.verification.verificationSuccess'),
      });
      
      // Close modal
      setShowVerificationMessage(false);
      onClose();
      
      // Reload page after a delay
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    }
  }, [isEmailVerified, showVerificationMessage, onClose, t, toast]);

  const handleModeSwitch = (newMode: 'login' | 'signup') => {
    setCurrentMode(newMode);
    if (onModeChange) {
      onModeChange(newMode);
    }
    // Clear form when switching modes
    setEmail('');
    setPassword('');
    setError(null);
    setShowVerificationMessage(false);
    setRegisteredPassword('');
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (currentMode === 'login') {
        await signInWithEmail(email, password);
        // Small delay to ensure Firebase syncs
        await new Promise(resolve => setTimeout(resolve, 1000));
        // Refresh user data to get the latest email verification status
        await refreshUser();
        
        // Check if this was a recently registered email that just got verified
        const wasJustVerified = registeredEmail === email && showVerificationMessage;
        
        if (wasJustVerified) {
          // This is a newly verified account
          toast({
            title: t('auth.verification.emailVerified'),
            description: t('auth.verification.verificationSuccess'),
          });
          
          // Clear verification state
          setShowVerificationMessage(false);
          setRegisteredEmail('');
          
          // Reload page to ensure clean state
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        } else {
          // Regular login
          toast({
            title: t('auth.modal.welcomeBack'),
            description: t('auth.modal.signInSuccess'),
          });
        }
        
        onClose();
      } else {
        // For signup, we don't sign in immediately - just show verification message
        await signUpWithEmail(email, password);
        setRegisteredEmail(email);
        setRegisteredPassword(password);
        setShowVerificationMessage(true);
        toast({
          title: t('auth.modal.accountCreated'),
          description: t('auth.verification.subtitle'),
        });
      }
    } catch (error: any) {
      // Handle email not verified error
      if (error.message === 'Please verify your email address before signing in.' || 
          (error.response && error.response.data && error.response.data.code === 'email-not-verified')) {
        setRegisteredEmail(email);
        setRegisteredPassword(password);
        setShowVerificationMessage(true);
        toast({
          title: t('auth.verification.title'),
          description: t('auth.verification.emailNotVerified'),
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }

      // Check if the error is due to wrong password, invalid credentials, or user not found
      if (error.code === 'auth/wrong-password' || 
          error.code === 'auth/invalid-credential' || 
          error.code === 'auth/user-not-found') {
        try {
          const signInMethods = await getSignInMethodsForEmail(email);
          if (signInMethods.includes('google.com')) {
            setError(t('auth.errors.googleEmailExists'));
            setLoading(false);
            return;
          }
        } catch (methodsError) {
          // If we can't check sign-in methods, fall through to original error
        }
      }

      // Handle account exists with different credential error
      if (error.code === 'auth/account-exists-with-different-credential') {
        toast({
          title: t('auth.modal.authError'),
          description: 'This email is already registered with a different sign-in method. Please use the original sign-in method.',
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      // Handle email already in use during signup
      if (error.code === 'auth/email-already-in-use') {
        try {
          const signInMethods = await getSignInMethodsForEmail(email);
          if (signInMethods.includes('google.com')) {
            setError(t('auth.errors.googleEmailExists'));
            setLoading(false);
            return;
          }
        } catch (methodsError) {
          // If we can't check sign-in methods, fall through to original error
        }
      }

      // Default error handling - show user-friendly message
      const getErrorMessage = (errorCode: string) => {
        switch (errorCode) {
          case 'auth/email-already-in-use':
            return t('auth.errors.emailAlreadyInUse');
          case 'auth/weak-password':
            return t('auth.errors.weakPassword');
          case 'auth/invalid-email':
            return t('auth.errors.invalidEmail');
          case 'auth/user-not-found':
            return t('auth.errors.userNotFound');
          case 'auth/wrong-password':
          case 'auth/invalid-credential':
            return t('auth.errors.wrongPassword');
          default:
            return t('auth.errors.default');
        }
      };

      setError(getErrorMessage(error.code));
    } finally {
      setLoading(false);
    }
  };

  const handleSocialAuth = async (provider: 'google' | 'microsoft' | 'apple') => {
    setLoading(true);
    setError(null);
    try {
      switch (provider) {
        case 'google':
          await signInWithGoogle();
          break;
        case 'microsoft':
          await signInWithMicrosoft();
          break;
        case 'apple':
          await signInWithApple();
          break;
      }
      // Small delay to ensure Firebase syncs
      await new Promise(resolve => setTimeout(resolve, 1000));
      // Refresh user data to get the latest email verification status
      await refreshUser();
      toast({
        title: t('auth.modal.welcome'),
        description: t('auth.modal.signInSuccess'),
      });
      onClose();
    } catch (error: any) {
      // Default error handling - show user-friendly message
      const getErrorMessage = (errorCode: string) => {
        switch (errorCode) {
          case 'auth/email-already-in-use':
            return t('auth.errors.emailAlreadyInUse');
          case 'auth/weak-password':
            return t('auth.errors.weakPassword');
          case 'auth/invalid-email':
            return t('auth.errors.invalidEmail');
          case 'auth/user-not-found':
            return t('auth.errors.userNotFound');
          case 'auth/wrong-password':
          case 'auth/invalid-credential':
            return t('auth.errors.wrongPassword');
          default:
            return t('auth.errors.default');
        }
      };

      setError(getErrorMessage(error.code));
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationComplete = () => {
    setShowVerificationMessage(false);
    onClose();
  };

  const handleResendVerification = async () => {
    if (!registeredEmail || !registeredPassword) return;
    
    setLoading(true);
    try {
      const { resendVerificationEmail } = await import('../lib/firebase');
      await resendVerificationEmail(registeredEmail, registeredPassword);
      toast({
        title: t('common.success'),
        description: t('auth.verification.resendSuccess'),
      });
    } catch (error) {
      console.error('Resend verification error:', error);
      toast({
        title: t('common.error'),
        description: t('auth.verification.resendError'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshVerification = () => {
    window.location.reload();
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetLoading(true);
    setError(null);

    try {
      await sendPasswordReset(resetEmail);
      setResetSuccess(true);
      toast({
        title: t('auth.passwordReset.successTitle'),
        description: t('auth.passwordReset.successMessage'),
      });
    } catch (error: any) {
      console.error('Password reset error:', error);
      
      const getResetErrorMessage = (errorCode: string) => {
        switch (errorCode) {
          case 'auth/user-not-found':
            return t('auth.errors.passwordResetEmailNotFound');
          case 'auth/too-many-requests':
            return t('auth.errors.passwordResetTooManyRequests');
          case 'auth/invalid-email':
            return t('auth.errors.invalidEmail');
          default:
            return t('auth.errors.passwordResetError');
        }
      };

      toast({
        title: t('common.error'),
        description: getResetErrorMessage(error.code),
        variant: 'destructive',
      });
    } finally {
      setResetLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowPasswordReset(false);
    setResetEmail('');
    setResetSuccess(false);
    setError(null);
  };

  // If showing verification message, render it instead of the main auth form
  if (showVerificationMessage) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <svg className="h-6 w-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              {t('auth.verification.title')}
            </DialogTitle>
            <DialogDescription>
              {t('auth.verification.subtitle')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    {t('auth.verification.checkEmail')}
                  </h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>{t('auth.verification.description')}</p>
                    {registeredEmail && (
                      <p className="mt-1 font-medium text-blue-800">
                        {registeredEmail}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col space-y-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleResendVerification}
                disabled={loading}
                className="w-full"
              >
                {loading ? (
                  <>
                    <svg className="mr-2 h-4 w-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {t('common.loading')}
                  </>
                ) : (
                  t('auth.verification.resendButton')
                )}
              </Button>

              <Button
                type="button"
                variant="default"
                onClick={handleRefreshVerification}
                disabled={loading}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                {loading ? (
                  <>
                    <svg className="mr-2 h-4 w-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {t('common.checking')}...
                  </>
                ) : (
                  <>
                    <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {t('auth.verification.refreshStatus')}
                  </>
                )}
              </Button>

              <Button
                type="button"
                onClick={onClose}
                className="w-full bg-[#EA580C] hover:bg-[#C2410C]"
              >
                {t('auth.modal.continueExploring')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // If showing password reset, render it instead of the main auth form
  if (showPasswordReset) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[400px] max-w-[95vw]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <svg className="h-6 w-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1721 9z" />
              </svg>
              {t('auth.passwordReset.title')}
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              {t('auth.passwordReset.description')}
            </DialogDescription>
          </DialogHeader>

          {resetSuccess ? (
            // Success message
            <div className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-green-800">
                      {t('auth.passwordReset.successTitle')}
                    </h3>
                    <div className="mt-2 text-sm text-green-700">
                      <p>{t('auth.passwordReset.successMessage')}</p>
                      <p className="mt-2 text-xs">{t('auth.passwordReset.checkSpam')}</p>
                    </div>
                  </div>
                </div>
              </div>

              <Button
                type="button"
                onClick={handleBackToLogin}
                className="w-full bg-[#EA580C] hover:bg-[#C2410C]"
              >
                {t('auth.passwordReset.backToLogin')}
              </Button>
            </div>
          ) : (
            // Reset form
            <div className="space-y-6">
              <form onSubmit={handlePasswordReset} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="resetEmail">{t('auth.modal.email')}</Label>
                  <Input
                    id="resetEmail"
                    type="email"
                    placeholder={t('auth.passwordReset.emailPlaceholder')}
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                    className="w-full"
                  />
                </div>

                <div className="flex flex-col space-y-3 pt-2">
                  <Button
                    type="submit"
                    className="w-full bg-[#EA580C] hover:bg-[#C2410C]"
                    disabled={resetLoading}
                  >
                    {resetLoading ? t('auth.modal.loading') : t('auth.passwordReset.sendResetEmail')}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleBackToLogin}
                    className="w-full"
                    disabled={resetLoading}
                  >
                    {t('auth.passwordReset.backToLogin')}
                  </Button>
                </div>
              </form>
            </div>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {currentMode === 'login' ? t('auth.modal.loginTitle') : t('auth.modal.signupTitle')}
          </DialogTitle>
          <DialogDescription>
            {currentMode === 'login' 
              ? t('auth.modal.loginDescription')
              : t('auth.modal.signupDescription')}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="text-red-500 text-sm mb-4">
            {error}
          </div>
        )}

        <div className="space-y-4">
          {/* Social Authentication Buttons */}
          <div className="space-y-2">
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => handleSocialAuth('google')}
              disabled={loading}
            >
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              {t('auth.modal.continueWithGoogle')}
            </Button>

            

            
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <Separator className="w-full" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                {t('auth.modal.orContinueWithEmail')}
              </span>
            </div>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleEmailAuth} className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="email">{t('auth.modal.email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t('auth.modal.enterEmail')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="password">{t('auth.modal.password')}</Label>
              <Input
                id="password"
                type="password"
                placeholder={t('auth.modal.enterPassword')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              {/* Forgot Password Link - only show in login mode */}
              {currentMode === 'login' && (
                <div className="text-right">
                  <Button
                    type="button"
                    variant="link"
                    className="text-sm text-[#EA580C] hover:text-[#C2410C] p-0 h-auto"
                    onClick={() => setShowPasswordReset(true)}
                  >
                    {t('auth.modal.forgotPassword')}
                  </Button>
                </div>
              )}
            </div>
            <Button type="submit" className="w-full bg-[#EA580C] hover:bg-[#C2410C]" disabled={loading}>
              {loading ? t('auth.modal.loading') : currentMode === 'login' ? t('auth.modal.signIn') : t('auth.modal.createAccount')}
            </Button>
          </form>

          {/* Mode Switch Navigation */}
          <div className="text-center pt-4 border-t border-neutral-200">
            <p className="text-sm text-neutral-600 mb-2">
              {currentMode === 'login' ? t('auth.modal.switchToSignup') : t('auth.modal.switchToLogin')}
            </p>
            <Button
              type="button"
              variant="link"
              className="text-[#EA580C] hover:text-[#C2410C] font-medium p-0 h-auto"
              onClick={() => handleModeSwitch(currentMode === 'login' ? 'signup' : 'login')}
              disabled={loading}
            >
              {currentMode === 'login' ? t('auth.modal.signUpLink') : t('auth.modal.loginLink')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};